import React, {JSXElementConstructor, useContext} from 'react';
import {GroupAddIcon} from '../icons/material/GroupAdd';
import {PeopleIcon} from '../icons/material/People';
import {FileDownloadDoneIcon} from '../icons/material/FileDownloadDone';
import {
  DatabaseNotification,
  DatabaseNotificationAction,
} from './database-notification';
import {MixedImage} from '../ui/images/mixed-image';
import {Button} from '../ui/buttons/button';
import {SiteConfigContext} from '../core/settings/site-config-context';
import {Line} from './notification-line';
import {SvgIconProps} from '../icons/svg-icon';
import clsx from 'clsx';
import {useMarkNotificationsAsRead} from './requests/use-mark-notifications-as-read';
import {useNavigate} from '../utils/hooks/use-navigate';
import {isAbsoluteUrl} from '../utils/urls/is-absolute-url';
import {Link} from 'react-router-dom';
import {useSettings} from '../core/settings/use-settings';
import {resolveNotificationAction} from './resolve-notification-action';

const iconMap = {
  'group-add': GroupAddIcon,
  people: PeopleIcon,
  'export-csv': FileDownloadDoneIcon,
} as Record<string, JSXElementConstructor<SvgIconProps>>;

function visibleNotificationLines(notification: DatabaseNotification) {
  if (!notification.type.includes('SendPackageReceivedNotif')) {
    return notification.data.lines;
  }
  return notification.data.lines.filter(line => line.type !== 'secondary');
}

interface NotificationListProps {
  notifications: DatabaseNotification[];
  className?: string;
}
export function NotificationList({
  notifications,
  className,
}: NotificationListProps) {
  const {notifications: config} = useContext(SiteConfigContext);

  return (
    <div className={className}>
      {notifications.map((notification, index) => {
        const isLast = notifications.length - 1 === index;
        const Renderer =
          config?.renderMap?.[notification.type] || NotificationListItem;
        return (
          <Renderer
            key={notification.id}
            notification={notification}
            isLast={isLast}
          />
        );
      })}
    </div>
  );
}

export interface NotificationListItemProps {
  notification: DatabaseNotification;
  onActionButtonClick?: ButtonActionsProps['onActionClick'];
  lineIconRenderer?: JSXElementConstructor<{icon: string}>;
  isLast: boolean;
}
export function NotificationListItem({
  notification,
  onActionButtonClick,
  lineIconRenderer,
  isLast,
}: NotificationListItemProps) {
  const markAsRead = useMarkNotificationsAsRead();
  const navigate = useNavigate();
  const mainAction = notification.data.mainAction;
  const mainActionPath = resolveNotificationAction(mainAction?.action);

  const showUnreadIndicator = !notification.data.image && !notification.read_at;

  return (
    <div
      onClick={() => {
        if (!markAsRead.isLoading && !notification.read_at) {
          markAsRead.mutate({ids: [notification.id]});
        }
        if (mainActionPath) {
          if (isAbsoluteUrl(mainActionPath)) {
            window.open(mainActionPath, '_blank')?.focus();
          } else {
            navigate(mainActionPath);
          }
        }
      }}
      className={clsx(
        'flex items-start gap-10 px-16 py-12 bg-alt',
        !isLast && 'border-b border-divider',
        mainActionPath && 'cursor-pointer',
        !notification.read_at
          ? 'bg-paper hover:bg-primary/10'
          : 'hover:bg-hover'
      )}
      title={mainAction?.label ? mainAction.label : undefined}
    >
      {showUnreadIndicator && (
        <div
          className="mt-5 h-8 w-8 shrink-0 rounded-full bg-primary"
          aria-hidden
        />
      )}
      {notification.data.image && (
        <MixedImage
          className="h-24 w-24 shrink-0 text-muted"
          src={iconMap[notification.data.image] || notification.data.image}
        />
      )}
      <div className="min-w-0 flex-1">
        {visibleNotificationLines(notification).map((line, index) => (
          <Line
            iconRenderer={lineIconRenderer}
            notification={notification}
            line={line}
            index={index}
            key={index}
          />
        ))}
        <ButtonActions
          onActionClick={onActionButtonClick}
          notification={notification}
        />
      </div>
    </div>
  );
}

interface ButtonActionsProps {
  notification: DatabaseNotification;
  onActionClick?: (
    e: React.MouseEvent,
    action: DatabaseNotificationAction
  ) => void;
}
function ButtonActions({notification, onActionClick}: ButtonActionsProps) {
  const {base_url} = useSettings();
  if (!notification.data.buttonActions) return null;

  // if there's no action handler provided, assume action is internal url and render a link
  return (
    <div className="mt-8 flex flex-wrap items-center gap-8">
      {notification.data.buttonActions.map((action, index) => {
        const resolved = resolveNotificationAction(action.action) || action.action;
        return (
          <Button
            key={index}
            size="xs"
            variant={index === 0 ? 'flat' : 'outline'}
            color={index === 0 ? 'primary' : null}
            elementType={!onActionClick ? Link : undefined}
            to={!onActionClick ? resolved.replace(base_url, '') : undefined}
            onClick={e => {
              onActionClick?.(e, {...action, action: resolved});
            }}
          >
            {action.label}
          </Button>
        );
      })}
    </div>
  );
}
